/**
 * RiskProfileSummary — 4 stat tiles giving a glance at the evaluation's
 * risk profile.
 *
 *   1. Total risks
 *   2. By status   — OPEN / MITIGATING / ACCEPTED / RESOLVED counts
 *   3. By severity — Low / Medium / High counts (matches HeatMap buckets)
 *   4. Top 3       — highest-impact (score desc) risks with site labels
 *
 * Linear: MYD-213 (G2). Reuses `comparisonByEvaluation[evalId]` so it
 * shares the fetch with HeatMap (G1). Standalone-ready — dispatches its
 * own fetchRisksComparison on mount.
 */

import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	ShieldAlert,
	ListChecks,
	Activity,
	Flame,
	Building2,
} from 'lucide-react'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

import { fetchRisksComparison } from 'slices/riskSlice'

const STATUS_ORDER = ['OPEN', 'MITIGATING', 'ACCEPTED', 'RESOLVED'] as const

export const STATUS_CHIP: Record<string, string> = {
	OPEN: 'bg-red-100 text-red-700',
	MITIGATING: 'bg-amber-100 text-amber-700',
	ACCEPTED: 'bg-blue-100 text-blue-700',
	RESOLVED: 'bg-emerald-100 text-emerald-700',
}

function severityFromScore(score: number): 'low' | 'medium' | 'high' {
	if (score <= 4) return 'low'
	if (score <= 15) return 'medium'
	return 'high'
}

interface RiskProfileSummaryProps {
	evalId: string
}

const RiskProfileSummary = ({ evalId }: RiskProfileSummaryProps) => {
	const dispatch = useDispatch()
	const comparison: any[] = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const allRisks = useMemo(() => {
		const flat: any[] = []
		for (const site of comparison) {
			for (const r of site?.risks ?? []) {
				flat.push({ ...r, siteName: site.siteName, site: String(site.siteId) })
			}
		}
		return flat
	}, [comparison])

	const totals = useMemo(() => {
		const byStatus: Record<string, number> = {
			OPEN: 0,
			MITIGATING: 0,
			ACCEPTED: 0,
			RESOLVED: 0,
		}
		const bySeverity = { low: 0, medium: 0, high: 0 }
		for (const r of allRisks) {
			const status = (r.status as string) || 'OPEN'
			byStatus[status] = (byStatus[status] ?? 0) + 1
			const sev = severityFromScore(Number(r.likelihood) * Number(r.impact))
			bySeverity[sev]++
		}
		return { byStatus, bySeverity, total: allRisks.length }
	}, [allRisks])

	const top3 = useMemo(() => {
		// Sort by score desc, then by name for stable ordering at ties.
		return allRisks
			.slice()
			.sort((a, b) => {
				const scoreDiff =
					Number(b.likelihood) * Number(b.impact) -
					Number(a.likelihood) * Number(a.impact)
				if (scoreDiff !== 0) return scoreDiff
				return String(a.name ?? '').localeCompare(String(b.name ?? ''))
			})
			.slice(0, 3)
	}, [allRisks])

	return (
		<div className="grid grid-cols-1 md:grid-cols-4 gap-3">
			{/* Tile 1: Total */}
			<Card>
				<CardHeader className="pb-2">
					<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
						<ShieldAlert className="w-3.5 h-3.5" />
						Total risks
					</h4>
				</CardHeader>
				<CardContent>
					<div className="text-3xl font-bold text-gray-900 tabular-nums">{totals.total}</div>
					<p className="text-[11px] text-gray-500 mt-0.5">
						across {comparison.length} site{comparison.length === 1 ? '' : 's'}
					</p>
				</CardContent>
			</Card>

			{/* Tile 2: By status */}
			<Card>
				<CardHeader className="pb-2">
					<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
						<ListChecks className="w-3.5 h-3.5" />
						By status
					</h4>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-2 gap-1.5">
						{STATUS_ORDER.map((s) => (
							<div
								key={s}
								className={cn(
									'flex items-center justify-between px-2 py-1 rounded text-xs',
									STATUS_CHIP[s],
								)}
							>
								<span className="font-medium">{s}</span>
								<span className="font-semibold tabular-nums">{totals.byStatus[s] ?? 0}</span>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			{/* Tile 3: By severity */}
			<Card>
				<CardHeader className="pb-2">
					<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
						<Activity className="w-3.5 h-3.5" />
						By severity
					</h4>
				</CardHeader>
				<CardContent>
					<div className="space-y-1.5">
						<SeverityRow label="High" colour="bg-red-500" count={totals.bySeverity.high} total={totals.total} />
						<SeverityRow label="Medium" colour="bg-amber-500" count={totals.bySeverity.medium} total={totals.total} />
						<SeverityRow label="Low" colour="bg-emerald-500" count={totals.bySeverity.low} total={totals.total} />
					</div>
				</CardContent>
			</Card>

			{/* Tile 4: Top 3 */}
			<Card>
				<CardHeader className="pb-2">
					<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
						<Flame className="w-3.5 h-3.5" />
						Highest impact
					</h4>
				</CardHeader>
				<CardContent>
					{top3.length === 0 ? (
						<p className="text-xs text-gray-400 italic py-2">No risks logged.</p>
					) : (
						<ol className="space-y-1.5">
							{top3.map((r) => {
								const score = Number(r.likelihood) * Number(r.impact)
								const sev = severityFromScore(score)
								const sevColour =
									sev === 'high'
										? 'bg-red-100 text-red-700'
										: sev === 'medium'
											? 'bg-amber-100 text-amber-700'
											: 'bg-emerald-100 text-emerald-700'
								return (
									<li key={r._id} className="flex items-start gap-2 text-xs">
										<Badge className={cn('text-[10px] tabular-nums w-7 justify-center', sevColour)}>
											{score}
										</Badge>
										<div className="flex-1 min-w-0">
											<div className="font-medium text-gray-900 truncate">
												{r.name ?? r.description ?? 'Untitled risk'}
											</div>
											<div className="text-[10px] text-gray-500 truncate flex items-center gap-1">
												<Building2 className="w-2.5 h-2.5" />
												{r.siteName}
											</div>
										</div>
									</li>
								)
							})}
						</ol>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

const SeverityRow = ({
	label,
	colour,
	count,
	total,
}: {
	label: string
	colour: string
	count: number
	total: number
}) => {
	const pct = total > 0 ? (count / total) * 100 : 0
	return (
		<div className="flex items-center gap-2 text-xs">
			<span className="w-12 text-gray-700">{label}</span>
			<div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
				<div
					className={cn('h-full rounded-full transition-all', colour)}
					style={{ width: `${pct}%` }}
				/>
			</div>
			<span className="w-6 text-right font-semibold text-gray-900 tabular-nums">{count}</span>
		</div>
	)
}

export default RiskProfileSummary
